import type { CMSCategoryBannerItem } from '@/lib/cms/types';
import type {
  CategoryBannerItem,
  ParallaxOverlayColorPreset,
  TextColorPreset,
} from '@/lib/contentful/types';
import { getAssetUrl } from '@/lib/contentful/utils';
import type { NormalizedBannerData } from './CategoryBannerItem.types';

export const TEXT_COLOR_MAP: Record<Exclude<TextColorPreset, 'custom'>, string> = {
  white: '#FFFFFF',
  black: '#1A1A1A',
  gold: '#C9A961',
};

export const PARALLAX_OVERLAY_COLOR_MAP: Record<
  Exclude<ParallaxOverlayColorPreset, 'custom'>,
  string
> = {
  green: '#3E4827',
  'dark-green': '#24261F',
  orange: '#F68D2E',
  brown: '#7F3812',
  black: '#000000',
  white: '#FFFFFF',
};

export const getEffectiveTransition = (
  transitionType: string,
  gridTransition?: 'none' | 'fade' | 'stagger' | null
): string => {
  if (transitionType !== 'none') return transitionType;
  if (gridTransition && gridTransition !== 'none') return 'fade';
  return 'none';
};

export const normalizeBannerData = (
  banner: CategoryBannerItem | CMSCategoryBannerItem
): NormalizedBannerData => {
  const cmsItem = banner as CMSCategoryBannerItem;
  const rawItem = banner as CategoryBannerItem;

  let backgroundImageUrl: string | undefined;
  let backgroundVideoUrl: string | undefined;

  if (cmsItem.media?.url) {
    backgroundImageUrl = cmsItem.media.url;
  } else {
    const backgroundMedia = rawItem.backgroundMedia || rawItem.backgroundImage;
    backgroundImageUrl = getAssetUrl(backgroundMedia?.image) || undefined;
    backgroundVideoUrl = getAssetUrl(backgroundMedia?.video) || undefined;
  }

  const hasVideo = Boolean(backgroundVideoUrl);
  const hasImage = Boolean(backgroundImageUrl);
  const hasMedia = hasVideo || hasImage;
  const backgroundColor = banner.backgroundColor;

  let productImageUrl: string | undefined;
  let productImageAlt: string;

  if (cmsItem.productImage?.url) {
    productImageUrl = cmsItem.productImage.url;
    productImageAlt = cmsItem.productImage.alt || banner.title || '';
  } else {
    productImageUrl = getAssetUrl(rawItem.productImage?.image) || undefined;
    productImageAlt = rawItem.productImage?.alt || banner.title || '';
  }

  let textColor: string | undefined;
  if (banner.textColorPreset && banner.textColorPreset !== 'custom') {
    const preset = banner.textColorPreset as keyof typeof TEXT_COLOR_MAP;
    if (preset in TEXT_COLOR_MAP) {
      textColor = TEXT_COLOR_MAP[preset];
    }
  } else if (banner.textColorCustom) {
    textColor = banner.textColorCustom;
  }

  const hoverEffect = banner.hoverEffect || 'zoom';
  const transitionType = banner.transitionType || 'none';
  const overlayOpacity = banner.overlayOpacity ?? 0.3;
  const textAlignment = banner.textAlignment || 'center';
  const contentPosition = banner.contentPosition || 'bottom';

  let parallaxOverlayColor: string | undefined;
  if (banner.parallaxOverlayColorPreset && banner.parallaxOverlayColorPreset !== 'custom') {
    const preset = banner.parallaxOverlayColorPreset as keyof typeof PARALLAX_OVERLAY_COLOR_MAP;
    if (preset in PARALLAX_OVERLAY_COLOR_MAP) {
      parallaxOverlayColor = PARALLAX_OVERLAY_COLOR_MAP[preset];
    }
  } else if (banner.parallaxOverlayColorCustom) {
    parallaxOverlayColor = banner.parallaxOverlayColorCustom;
  }
  if (!parallaxOverlayColor) {
    parallaxOverlayColor = PARALLAX_OVERLAY_COLOR_MAP['dark-green'];
  }

  const href = cmsItem.cta?.url || rawItem.href || '#';
  const ctaText = cmsItem.cta?.text || rawItem.ctaText;
  const ctaColor = cmsItem.ctaColor || rawItem.ctaColor || null;

  return {
    backgroundImageUrl,
    backgroundVideoUrl,
    hasVideo,
    hasImage,
    hasMedia,
    backgroundColor,
    productImageUrl,
    productImageAlt,
    textColor,
    hoverEffect,
    transitionType,
    overlayOpacity,
    textAlignment,
    contentPosition,
    parallaxOverlayColor,
    href,
    ctaText,
    ctaColor,
  };
};
